import React from "react";
import PropTypes from "prop-types";
import styles from "./TabBar.module.css";

/**
 * Shared tab strip used across the BSM panels (Store, Order Management,
 * Statistics, Settings) and the product version tabs. Renders dark navy tabs
 * sitting on a thick navy baseline; the active tab is light and breaks the
 * line, connecting into the content area below.
 *
 * @param {{ key: string, label: React.ReactNode, dot?: string }[]} tabs
 *        Tab descriptors. `dot` (optional) is a CSS color for a status dot,
 *        used by the version tabs.
 * @param {string} activeKey            The key of the active tab.
 * @param {(key: string) => void} onChange  Fired when a tab is clicked.
 * @param {(key: string) => void} [onRename]  Fired on double-click (rename).
 * @param {React.ReactNode} [actions]   Optional trailing controls (e.g. the
 *        version add/delete buttons), pinned to the right of the strip.
 * @param {string} [className]          Extra class on the outer strip.
 */
export default function TabBar({
  tabs,
  activeKey,
  onChange,
  onRename,
  actions,
  className = "",
}) {
  return (
    <div className={`${styles.tabsBar} ${className}`}>
      <div className={styles.tabs}>
        {tabs.map((tab) => {
          const isActive = tab.key === activeKey;
          return (
            <button
              key={tab.key}
              type="button"
              className={`${styles.tab} ${isActive ? styles.active : ""}`}
              onClick={() => onChange?.(tab.key)}
              onDoubleClick={onRename ? () => onRename(tab.key) : undefined}
            >
              {tab.dot && (
                <span
                  className={styles.dot}
                  style={{ backgroundColor: tab.dot }}
                />
              )}
              <span className={styles.label}>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {actions != null && <div className={styles.actions}>{actions}</div>}
    </div>
  );
}

TabBar.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      dot: PropTypes.string,
    })
  ).isRequired,
  activeKey: PropTypes.string,
  onChange: PropTypes.func,
  onRename: PropTypes.func,
  actions: PropTypes.node,
  className: PropTypes.string,
};
